import { INVALID_TOKEN, NO_TOKEN } from './errors';

// authorize() answers 401 with one of these codes (server/misc/auth/subjectErrors.ts); the
// legacy strings are still sent by the documentation middleware.
const REJECTED_SESSION_CODES = ['NO_TOKEN', 'INVALID_TOKEN', 'USER_INACTIVE', NO_TOKEN, INVALID_TOKEN];

let onSessionRejected: (() => void) | null = null;

/**
 * True if an `authorizationError` from a 401 body means the session is no longer valid
 * (missing/expired/invalid token or inactive user), as opposed to any other 401.
 * @param authorizationError The `authorizationError` field of the response body.
 */
export const isRejectedSession = (authorizationError: unknown): boolean => (
  typeof authorizationError === 'string' && REJECTED_SESSION_CODES.includes(authorizationError)
);

/**
 * Registers the callback run when any request reports a rejected session.
 * @param handler Logs the user out; null clears it.
 */
export const setSessionRejectedHandler = (handler: (() => void) | null): void => {
  onSessionRejected = handler;
};

/**
 * Wraps fetch so a 401 carrying a rejected-session code runs the registered handler.
 * The response is returned untouched, so callers still see the error.
 * @param baseFetch The fetch implementation to wrap.
 */
export const withSessionCheck = (baseFetch: typeof fetch): typeof fetch => async (...args) => {
  const response = await baseFetch(...args);
  if (response.status === 401 && onSessionRejected) {
    const body = await response.clone().json().catch(() => null);
    if (isRejectedSession(body?.authorizationError)) onSessionRejected();
  }
  return response;
};
